// v49.24 — Recent-nav hooks. Effect hook records the current route
// whenever it changes; reader hook returns the latest list + subscribes
// to the cross-tab change event so cards re-render after navigation.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useShellUser } from "@/features/auth/AuthContext";
import { getRecent, trackRecent, type RecentEntry } from "./recentNav";

/* Resolve a human-friendly label for a path. Detail routes get their
   :id suffix so the entry isn't ambiguous. */
function deriveLabel(pathname: string): string {
  if (pathname.startsWith("/projects/")) return "Project profile";
  if (pathname.startsWith("/partners/")) return "Partner profile";
  if (pathname.startsWith("/requirements/")) return "Requirement";
  if (pathname.startsWith("/deals/")) return "Placement";
  if (pathname.startsWith("/workers/")) return "Operative";
  return pathname;
}

/* Mounted at app shell level — records every route change. */
export function useTrackRecentNav(): void {
  const { pathname } = useLocation();
  const user = useShellUser();
  useEffect(() => {
    if (!user) return;
    trackRecent(user.id, pathname, deriveLabel(pathname));
  }, [pathname, user]);
}

/* Read the current list. Auto-refreshes when track fires. */
export function useRecentNav(): RecentEntry[] {
  const user = useShellUser();
  const [list, setList] = useState<RecentEntry[]>(() => getRecent(user?.id));
  useEffect(() => {
    const refresh = () => setList(getRecent(user?.id));
    refresh();
    window.addEventListener("lc-recent-nav-changed", refresh);
    return () => window.removeEventListener("lc-recent-nav-changed", refresh);
  }, [user?.id]);
  return list;
}
